import { ClaimTransmissionStatus, ClaimTransmissionStatusType } from '../../../../api';
import { ITimelineEventStatusTagTone } from '../claim-timeline.types';

/** CLM02 monetary amount from an 837 X12 payload. Handles LF and CRLF segment terminators. */
export function parseTransmissionClmAmount(x12?: string | null): string {
    const clmMatch = (x12 || '').match(/CLM\*[^\r\n]*/);
    if (!clmMatch) {
        return '0';
    }

    const clmAmount = clmMatch[0].split('*')[2];
    return clmAmount ? parseFloat(clmAmount).toFixed(2) : '0';
}

export interface ITransmissionPayerRef {
    id?: string;
    created?: string;
    clearinghousePayerId?: string;
    payerSubscriberId?: string;
}

export function getTransmissionPayerKey(transmission: ITransmissionPayerRef): string | undefined {
    const clearinghousePayerId = transmission.clearinghousePayerId?.trim();
    const payerSubscriberId = transmission.payerSubscriberId?.trim();
    if (!clearinghousePayerId && !payerSubscriberId) {
        return undefined;
    }

    return `${clearinghousePayerId || ''}|${payerSubscriberId || ''}`;
}

/**
 * Previous 837 for the same payer, newest-first. Does not pair a secondary
 * transmission with a prior primary transmission.
 */
export function findPreviousTransmissionForSamePayer<T extends ITransmissionPayerRef>(
    current: T,
    transmissions: T[],
): T | undefined {
    const payerKey = getTransmissionPayerKey(current);
    if (!payerKey || !current.id) {
        return undefined;
    }

    const samePayer = transmissions
        .filter(transmission => transmission.id && getTransmissionPayerKey(transmission) === payerKey)
        .sort(
            (left, right) =>
                new Date(right.created || 0).getTime() - new Date(left.created || 0).getTime(),
        );

    const currentIndex = samePayer.findIndex(transmission => transmission.id === current.id);
    if (currentIndex < 0 || currentIndex >= samePayer.length - 1) {
        return undefined;
    }

    return samePayer[currentIndex + 1];
}

export function transmissionStatusToTone(
    status?: ClaimTransmissionStatusType,
): ITimelineEventStatusTagTone {
    switch (status) {
        case ClaimTransmissionStatusType.SUBMITTED:
            return 'enter';
        case ClaimTransmissionStatusType.ACKNOWLEDGED:
            return 'pending';
        case ClaimTransmissionStatusType.COMPLETED:
            return 'success';
        case ClaimTransmissionStatusType.REJECTED:
        case ClaimTransmissionStatusType.DENIED:
            return 'attention';
        case ClaimTransmissionStatusType.ADJUDICATED:
        case ClaimTransmissionStatusType.FORWARDED:
            return 'accent';
        default:
            return 'neutral';
    }
}

export function formatTransmissionResponseMessage(status: ClaimTransmissionStatus): string {
    if (status.messages) {
        return status.messages;
    }

    if (status.status === ClaimTransmissionStatusType.SUBMITTED) {
        return 'Transmission submitted to clearinghouse.';
    }

    return status.status || 'Status update received.';
}
